import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { apiService } from '../services/apiService';
import '../styles/top-movers.css';

interface TopMover {
  ticker: string;
  direction: string;
  total_premium: number;
  buy_pct: number;
  sell_pct: number;
  hottest_chain: string;
}

interface TopMoversAnalysis {
  analysis: string;
  analysis_date?: string;
  top_movers: TopMover[];
  last_updated: string;
  next_update: string;
  flows_analyzed: number;
  tickers_analyzed: number;
}

interface TopMoversPanelProps {
  selectedDate?: string;  // Optional: YYYY-MM-DD format
}

export const TopMoversPanel: React.FC<TopMoversPanelProps> = ({ selectedDate }) => {
  const [isExpanded, setIsExpanded] = useState(false);  // Collapsed by default
  const [analysis, setAnalysis] = useState<TopMoversAnalysis | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<string>('');
  const previousDateRef = useRef<string | undefined>(undefined);

  const fetchAnalysis = useCallback(async (date?: string) => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getTopMoversAnalysis(date);
      setAnalysis(data);
    } catch (err: any) {
      console.error('Failed to fetch top movers analysis:', err);
      const errorMessage = err.response?.data?.detail?.message || err.message || 'Failed to load analysis';
      // Only show error if we don't have existing analysis to display
      setAnalysis((prevAnalysis) => {
        if (!prevAnalysis) {
          setError(errorMessage);
        }
        return prevAnalysis;
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch when selectedDate changes or on initial mount
  useEffect(() => {
    // Only fetch if date actually changed
    if (previousDateRef.current !== selectedDate) {
      previousDateRef.current = selectedDate;
      fetchAnalysis(selectedDate);
    }
  }, [selectedDate, fetchAnalysis]);

  // Initial fetch on mount
  useEffect(() => {
    fetchAnalysis(selectedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-refresh every 60 minutes (only for today's analysis)
  useEffect(() => {
    // Don't auto-refresh if viewing historical date
    if (selectedDate) return;

    const interval = setInterval(() => {
      fetchAnalysis(undefined);
    }, 60 * 60 * 1000); // 60 minutes

    return () => clearInterval(interval);
  }, [fetchAnalysis, selectedDate]);

  // Countdown timer
  useEffect(() => {
    if (!analysis?.next_update) return;

    const updateCountdown = () => {
      const nextUpdate = new Date(analysis.next_update);
      const now = new Date();
      const diff = nextUpdate.getTime() - now.getTime();

      if (diff <= 0) {
        setCountdown('Updating soon...');
        return;
      }

      const minutes = Math.floor(diff / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      setCountdown(`${minutes}m ${seconds}s`);
    };

    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);
    return () => clearInterval(timer);
  }, [analysis?.next_update]);

  const formatPremium = (premium: number) => {
    if (premium >= 1000000) {
      return `$${(premium / 1000000).toFixed(1)}M`;
    }
    return `$${(premium / 1000).toFixed(0)}K`;
  };

  const getDirectionIcon = (direction: string) => {
    switch (direction.toUpperCase()) {
      case 'BULLISH':
        return '🟢';
      case 'BEARISH':
        return '🔴';
      default:
        return '⚪';
    }
  };

  const getDirectionClass = (direction: string) => {
    switch (direction.toUpperCase()) {
      case 'BULLISH':
        return 'direction-bullish';
      case 'BEARISH':
        return 'direction-bearish';
      default:
        return 'direction-neutral';
    }
  };

  const formatLastUpdated = () => {
    if (!analysis?.last_updated) return '';
    const date = new Date(analysis.last_updated);
    return date.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    }) + ' EST';
  };

  const formatAnalysisDate = () => {
    const dateStr = analysis?.analysis_date || selectedDate;
    if (!dateStr) return 'Today';
    const date = new Date(dateStr + 'T12:00:00');
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className={`top-movers-panel ${isExpanded ? 'expanded' : 'collapsed'}`}>
      {/* Header - Always Visible */}
      <div 
        className="top-movers-header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="header-left">
          <span className="header-icon">🏆</span>
          <h3 className="header-title">Top 5 Movers — AI Analysis</h3>
          {selectedDate && <span className="date-badge">{formatAnalysisDate()}</span>}
          {loading && <span className="loading-badge">Updating...</span>}
        </div>
        
        <div className="header-right">
          {analysis && !loading && (
            <>
              <div className="header-stats">
                <span className="stat-item">
                  <span className="stat-label">Last Updated:</span>
                  <span className="stat-value">{formatLastUpdated()}</span>
                </span>
                <span className="stat-divider">|</span>
                <span className="stat-item">
                  <span className="stat-label">Next Update:</span>
                  <span className="stat-value countdown">{countdown}</span>
                </span>
              </div>
            </>
          )}
          <button className="expand-toggle">
            {isExpanded ? '▼' : '▶'}
          </button>
        </div>
      </div>

      {/* Quick Summary Bar - Always Visible When Collapsed */}
      {!isExpanded && analysis && (
        <div className="quick-summary-bar">
          {analysis.top_movers.map((mover, index) => (
            <div key={mover.ticker} className="quick-mover-chip">
              <span className="chip-rank">#{index + 1}</span>
              <span className="chip-ticker">{mover.ticker}</span>
              <span className={`chip-direction ${getDirectionClass(mover.direction)}`}>
                {getDirectionIcon(mover.direction)}
              </span>
              <span className="chip-premium">{formatPremium(mover.total_premium)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Expanded Content */}
      {isExpanded && (
        <div className="top-movers-content">
          {loading && !analysis && (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading AI analysis...</p>
            </div>
          )}

          {error && !analysis && (
            <div className="error-state">
              <span className="error-icon">⚠️</span>
              <p>{error}</p>
              <button onClick={() => fetchAnalysis(selectedDate)} className="retry-btn">
                Retry
              </button>
            </div>
          )}

          {analysis && (
            <>
              {/* Mover Cards Grid */}
              <div className="movers-grid">
                {analysis.top_movers.map((mover, index) => (
                  <div key={mover.ticker} className="mover-card">
                    <div className="mover-rank">#{index + 1}</div>
                    <div className="mover-header">
                      <span className="mover-ticker">{mover.ticker}</span>
                      <span className={`mover-direction ${getDirectionClass(mover.direction)}`}>
                        {getDirectionIcon(mover.direction)} {mover.direction}
                      </span>
                    </div>
                    <div className="mover-chain">
                      <span className="chain-label">🔥</span>
                      <span className="chain-value">{mover.hottest_chain}</span>
                    </div>
                    <div className="mover-stats">
                      <div className="stat">
                        <span className="stat-label">Premium</span>
                        <span className="stat-value">{formatPremium(mover.total_premium)}</span>
                      </div>
                      <div className="stat">
                        <span className="stat-label">Buy</span>
                        <span className="stat-value buy">{mover.buy_pct.toFixed(1)}%</span>
                      </div>
                      <div className="stat">
                        <span className="stat-label">Sell</span>
                        <span className="stat-value sell">{mover.sell_pct.toFixed(1)}%</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Full Analysis Section */}
              <div className="analysis-section">
                <div className="analysis-header">
                  <span className="analysis-icon">🤖</span>
                  <h4>Full AI Analysis</h4>
                  <div className="analysis-meta">
                    <span>{analysis.flows_analyzed} flows analyzed</span>
                    <span>•</span>
                    <span>{analysis.tickers_analyzed} tickers</span>
                  </div>
                </div>
                <div className="analysis-body">
                  <ReactMarkdown 
                    remarkPlugins={[remarkGfm]}
                    components={{
                      h1: ({children}) => <h1 className="md-h1">{children}</h1>,
                      h2: ({children}) => <h2 className="md-h2">{children}</h2>,
                      h3: ({children}) => <h3 className="md-h3">{children}</h3>,
                      p: ({children}) => <p className="md-p">{children}</p>,
                      ul: ({children}) => <ul className="md-ul">{children}</ul>,
                      ol: ({children}) => <ol className="md-ol">{children}</ol>,
                      li: ({children}) => <li className="md-li">{children}</li>,
                      strong: ({children}) => <strong className="md-strong">{children}</strong>,
                      em: ({children}) => <em className="md-em">{children}</em>,
                      code: ({children, className}) => {
                        const isInline = !className;
                        return isInline ? (
                          <code className="md-code-inline">{children}</code>
                        ) : (
                          <code className="md-code-block">{children}</code>
                        );
                      },
                      blockquote: ({children}) => <blockquote className="md-blockquote">{children}</blockquote>,
                      hr: () => <hr className="md-hr" />,
                    }}
                  >
                    {analysis.analysis}
                  </ReactMarkdown>
                </div>
              </div>
            </>
          )}

          {/* Empty State */}
          {!loading && !error && !analysis && (
            <div className="empty-state">
              <span className="empty-icon">📊</span>
              <h4>No Analysis Available</h4>
              <p>AI analysis will be generated during market hours.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
